import React from 'react';
import { Helmet } from 'react-helmet';
import { App } from '@roku-web-core/app';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { enableMapSet } from 'immer';

import { Error4xx } from 'components/error/Error4xx';
import { getAppFeatures } from 'modules/app';
import MyApp from 'pages/_app';

import { routeTree } from 'routeTree.gen';

// Enable Map and Set in immer
enableMapSet();

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  basepath: 'smarthome',
  trailingSlash: 'never',
  context: {
    appFeatures: {
      events: false,
      lights: false,
    },
  },
  // defaultErrorComponent: Error5xx,
  defaultNotFoundComponent: Error4xx,
});

// Get resources from KS
const { version, features } = App.getResource();
const appFeatures = getAppFeatures(features);

// Is beta environment
const isBeta = /beta/.test(new URL(window?.location?.href)?.hostname);

// Initialize Sentry
Sentry.init({
  dsn: 'https://4c98c96cc657b89b92b347eedc1a35b6@o4508139216175104.ingest.us.sentry.io/4508143914909696',
  enabled: process.env.NODE_ENV === 'production',
  environment: isBeta ? 'beta' : process.env.NODE_ENV,
  release: version,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^\/api\//],
  // This is relative to tracesSampleRate,
  profilesSampleRate: 0.5,
  // Session Replay
  replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0 : 1.0,
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

// Register things for typesafety
declare module '@tanstack/react-router-devtools' {
  interface Register {
    router: typeof router;
  }
}

App.render({
  PageComponent: () => (
    <MyApp>
      <Helmet>
        <title>Roku Smart Home</title>
      </Helmet>
      <RouterProvider router={router} context={{ appFeatures }} />
    </MyApp>
  ),
});
